import * as React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  SimpleGrid,
  VStack,
  Image,
  Flex,
  useColorModeValue,
} from '@chakra-ui/react';
import { FaSeedling, FaHandshake, FaUsers, FaLeaf } from 'react-icons/fa';

interface Feature {
  icon: any; // Using any temporarily to resolve type issues
  title: string;
  description: string;
}

const AboutUs: React.FC = () => {
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');

  const features: Feature[] = [
    {
      icon: FaHandshake,
      title: 'Zero commission',
      description: "We don't take a cut. You negotiate and pay directly."
    },
    {
      icon: FaLeaf,
      title: 'Nationwide reach',
      description: 'Connect with farmers and buyers in rural and urban areas across Zimbabwe.'
    },
    {
      icon: FaUsers,
      title: 'Built for trust',
      description: 'Verified listings, clear product details and direct contact to build long-term business relationships.'
    },
    {
      icon: FaSeedling,
      title: 'For everyone in Agri',
      description: 'Small holder farmers, commercial farmers, agro dealers, processors, retailers and every day buyers.'
    }
  ];

  return (
    <Box bg={bgColor} py={20}>
      <Container maxW="container.xl">
        <VStack spacing={12}>
          {/* Hero Section */}
          <Box textAlign="center" mb={10}>
            <Heading as="h1" size="2xl" mb={6} color={useColorModeValue('brand.700', 'brand.200')}>
              About MarketLink
            </Heading>
            <Text fontSize="xl" color="gray.600" maxW="900px" mx="auto" lineHeight="tall">
              We are Zimbabwe's agro marketplace where farmers, agro dealers and buyers meet directly.
              Our platform connects farmers, equipment suppliers, input suppliers and buyers across Zimbabwe without the middlemen mess and commission fees.
            </Text>
          </Box>

          {/* Mission Section */}
          <Box py={10}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={10} alignItems="center">
              <Box>
                <Heading as="h2" size="xl" mb={6} color={useColorModeValue('brand.600', 'brand.200')}>
                  Why We Exist
                </Heading>
                <Text fontSize="lg" color="gray.600" mb={4} lineHeight="tall">
                  Agriculture feeds Zimbabwe, but too many farmers struggle to find buyers and too many buyers also struggle to find reliable suppliers. We close the gap with this user-friendly digital agro marketplace.
                </Text>
                <Text fontSize="lg" color="gray.600" lineHeight="tall">
                  This is more than just a marketplace. It's a platform to strengthen local food systems, reduce waste and put more value back into the hands of the people who grow and sell.
                </Text>
              </Box>
              <Box>
                <Image
                  src="/images/premium_photo-1661811677567-6f14477aa1fa.jpg"
                  alt="Zimbabwe farming"
                  borderRadius="lg"
                  objectFit="cover"
                  h="400px"
                  w="full"
                  shadow="lg"
                  fallbackSrc="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?ixlib=rb-4.0.3"
                />
              </Box>
            </SimpleGrid>
          </Box>

          {/* Features Section */}
          <Box py={10} w="full">
            <Heading as="h2" size="xl" mb={8} textAlign="center" color={useColorModeValue('brand.600', 'brand.200')}>
              What Makes Us Different
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10}>
              {features.map((feature, index) => (
                <Box
                  key={index}
                  bg={cardBg}
                  p={6}
                  borderRadius="lg"
                  shadow="md"
                  textAlign="center"
                >
                  <Flex
                    w={16}
                    h={16}
                    align="center"
                    justify="center"
                    rounded="full"
                    bg="green.50"
                    mb={4}
                    mx="auto"
                  >
                    <Box as={feature.icon} color="green.500" fontSize="2em" />
                  </Flex>
                  <Heading as="h3" size="md" mb={4}>
                    {feature.title}
                  </Heading>
                  <Text color="gray.600">
                    {feature.description}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          {/* How It Works Section */}
          <Box py={10} w="full" bg={cardBg} borderRadius="lg" p={8}>
            <Heading as="h2" size="xl" mb={8} textAlign="center" color={useColorModeValue('brand.600', 'brand.200')}>
              How It Works
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
              <VStack>
                <Box 
                  w={16} 
                  h={16} 
                  bg="purple.500" 
                  color="white" 
                  borderRadius="full" 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                  mb={4}
                >
                  1
                </Box>
                <Heading size="md" mb={2}>Register & List</Heading>
                <Text textAlign="center" color="gray.600">
                  Farmers register and list their products with prices, quantities and contact information. It's free and takes just minutes.
                </Text>
              </VStack>
              <VStack>
                <Box 
                  w={16} 
                  h={16} 
                  bg="green.500" 
                  color="white" 
                  borderRadius="full" 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                  mb={4}
                >
                  2
                </Box>
                <Heading size="md" mb={2}>Browse & Connect</Heading>
                <Text textAlign="center" color="gray.600">
                  Buyers browse available products, check market prices, and view farmer's contact details to arrange purchases directly.
                </Text>
              </VStack>
              <VStack>
                <Box 
                  w={16} 
                  h={16} 
                  bg="purple.500" 
                  color="white" 
                  borderRadius="full" 
                  display="flex" 
                  alignItems="center" 
                  justifyContent="center"
                  fontSize="2xl"
                  fontWeight="bold"
                  mb={4}
                >
                  3
                </Box>
                <Heading size="md" mb={2}>Trade Directly</Heading>
                <Text textAlign="center" color="gray.600">
                  Buyers and farmers negotiate and complete transactions directly, ensuring fair prices and building lasting business relationships.
                </Text>
              </VStack>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default AboutUs;